import { Injectable } from '@angular/core';
import { UICustomizationService } from '@ispm/shared/services/ui-customization.service';
import { filter } from 'rxjs/operators';
import { userInfo } from '@ispm/migration/components/login-component/component/login-const';
import { Router } from '@angular/router';
import { AuthenticationService } from '@ispm/user/services/authentication.service';
import { OrganizationService } from '@ispm/migration/services/organization.service';
import { RouterManagerService } from '@ispm/core/services/router-manager.service';
import { NavigationConfigService } from '@ispm/dashboard/navigation/services/navigationConfig.service';
import { UserActionCreatorService } from '@ispm/user/services/user-action-creator.service';
import { CryptoService } from '@ispm/migration/services/crypto.service';
import { EndpointService } from '@ispm/shared/services/endpoint.service';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Location } from '@angular/common';

@Injectable()
export class LoginAuthService {

  public endpointIspmAdmin;
  public isLoading$ = new BehaviorSubject<boolean>(false);

  constructor( private uiCustomizationService: UICustomizationService,
     private authService: AuthenticationService,
     private route: Router,
     private orgService: OrganizationService,
     private navigationConfigService: NavigationConfigService,
     private routerManagerService: RouterManagerService,
     private userActionCreatorService: UserActionCreatorService,
     private endpointService: EndpointService,
     private cryptoService: CryptoService,
     private location: Location,
     private router: Router
     ) { }

  /**
   * GetErrorObject
   *
   * return an object that handles an error that is being thrown while attempting to login.
   * This object handles the error and holds the error data
   *
   * @return object
   */
  public getErrorObject(callbackOnError: any) {

    const errObj = {
      loginError: false,
      loginErrorReason: null,
      redirectUri: null,

      // a function to be called when an error is caught
      errorHandler: function (error: any) {
        errObj.loginError = true; // indicate that an error occurred

        if (error.status === 403 && error.data && error.data.cause === '2fa') {
          errObj.redirectUri = error.data.redirect_uri;
        }

        // if a server error is thrown
        if (error.status === 500) {
          errObj.loginErrorReason = 'An error occurred, please try again in a few minutes';

          // if Bad Request error is thrown: if error data includes a specific message to display - display it.
          // Otherwise, display a general 'Bad Request' data
        } else if (error.status === 400) {

          if (error.data !== undefined && error.data != null) {
            errObj.loginErrorReason = error.data;
          } else {
            errObj.loginErrorReason = 'Bad Request';
          }

          // in any other case - display an 'Incorrect Credentials' error
        } else {
          errObj.loginErrorReason = 'Incorrect Credentials';
        }

        // after finishing processing the error and if a callback function is passed - invoke it
        if (callbackOnError !== null && callbackOnError !== undefined) {
          callbackOnError();
        }
      }
    };

    return errObj;
  }

  public redirectionToDashboard(response : any){
    this.isLoading$.next(true);
    const currUser = this.authService.user;
    this.uiCustomizationService.cleanUICustomResource();
    userInfo.orgInfo = currUser.organizations;
    userInfo.role = currUser.role;
    userInfo.info = currUser.info;
    userInfo.addl = { name: currUser.managingOrganization, id: currUser.organization };
    this.authService.audit(userInfo);

    if (!this.authService.isAdminUser() || !this.authService.doesUserHasPermission('ADMIN.LOGIN.USER')) {
      this.orgService.get(currUser.organization).subscribe(() => {
        let selectState = 'dashboard';
        let params = { };
        this.authService.setCurrentUser(this.authService.user);
        this.userActionCreatorService.forceSetOrganizationConfiguration();
        this.userActionCreatorService.setEnvironmentDetails();

        this.navigationConfigService
          .requestWebConfiguration()
          .pipe(filter((data: any) => data))
          .toPromise()
          .then((data : any) => {
            const selectedNav = data.navigation.selected[currUser.role];
            let locatedSelected = false;

            if (this.route.url !== '/dashboard/setting/base') {
              for (const navItem of data.navigation.items) {
                if (navItem.enable && selectedNav === navItem.identify) {
                  selectState = this.routerManagerService.getURL(navItem.redirectUrl);
                  params = this.routerManagerService.getQueryParams(navItem.redirectUrl);
                  locatedSelected = true;
                  if(navItem.redirectUrl !== "" && navItem.redirectUrl !== undefined) {
                    break;
                  }
                }
              }

              if (!selectedNav || !locatedSelected) {
                const firstEnable = data.navigation.items.find(navItem => !!navItem.enable);

                // no enabled navigation's item may be configured. in this case, the default selectState will be used
                if (firstEnable) {
                  selectState = this.routerManagerService.getURL(firstEnable.redirectUrl);
                  params = this.routerManagerService.getQueryParams(firstEnable.redirectUrl);
                }
              }
            } else if (this.authService.doesUserHasPermission('ADMIN.LOGIN.USER')) {
              selectState = 'admin/settings';
            } else {
              this.authService.logout({ refresh: true, callFromResponseHandler: true });
            }
            this.isLoading$.next(false);
            this.uiCustomizationService.storeUICustomResources(response?.uiCustomResources);
            this.route.navigate([selectState], {queryParams: params});
          });
      });
    } else if (response?.redirectUri) {
      try {
        window.open(response?.redirectUri, '_self');
      } catch (e) {
        console.log('Error while redirecting to hsdp page  ', e.message);
      }
    } else {
      this.gotoNewAdmin(currUser);
    }
  }

  gotoNewAdmin(currUser) {
    if (this.route.url ==='dashboard/settings/base') {
      this.route.navigate(['dashboard/settings'], { queryParams: { isAdminNav: true }});
    } else {
      this.endpointIspmAdmin = this.endpointService.getGenomicsIspmAdminEndpoint()
        .subscribe(
          data => {
            const hash = this.cryptoService.hashSha256(JSON.stringify(currUser.permissions.sort()) + '_' + (currUser.roleId || ''));
            if(window.location.origin == data.endpoint){
              this.router.navigateByUrl(`/admin/settings?u-code=${encodeURIComponent(currUser.xsrf)}&aux_token=${hash}`)
            } else{
              window.open(data.endpoint + '/admin/settings/?u-code=' + encodeURIComponent(currUser.xsrf) + '&aux_token=' + hash, '_self');
            }
          },
          err => {
            console.log('Error while loading admin  ', `${err.code} - ${err.message} - ${err.stack}`);
          }
        );
    }
  }

  destroy(){
    if (this.endpointIspmAdmin) {
      this.endpointIspmAdmin.unsubscribe();
    }
  }

}
